// src/components/forms/SteeringClockForm.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { Form, Input, Button, Select, App } from 'antd';
import { SteeringClockItem } from '../../types/ProductItems';
import RefOemManager from '../common/RefOemManager';
import ImageUploader from '../common/ImageUploader';
import { toBase64, mapBase64ToUploadList } from '../../utils/formsThumbnails';
import { fetchProductRefsOems } from '../../services/productAPI';

interface SteeringClockFormProps {
  onAdd: (item: SteeringClockItem) => void;
  initialValues?: SteeringClockItem | null;
}
const { Option } = Select;

const nonNegativeRule = (label: string) => ({
  validator(_: any, value: any) {
    if (value === undefined || value === '' || Number(value) >= 0) return Promise.resolve();
    return Promise.reject(new Error(`${label} не может быть отрицательным`));
  },
});

const SteeringClockForm: React.FC<SteeringClockFormProps> = ({ onAdd, initialValues }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [refList, setRefList] = useState<{ [brand: string]: string[] }>({});
  const [oemList, setOemList] = useState<{ [model: string]: string[] }>({});

  const resetForm = useCallback(() => {
    form.resetFields();
    setRefList({});
    setOemList({});
 }, [form]);

  useEffect(() => {
    let mounted = true;
    if (initialValues) {
      const { images = [], ...rest } = initialValues;
      form.setFieldsValue({
        ...rest,
        images: mapBase64ToUploadList(images),
      });
      setRefList(initialValues.references ?? {});
      setOemList(initialValues.oems ?? {});
      const mysqlId = Number(initialValues.id);
      if (Number.isInteger(mysqlId) && mysqlId > 0) {
        fetchProductRefsOems('airbag_cables', mysqlId).then(fresh => {
          if (fresh && mounted) {
            setRefList(fresh.references);
            setOemList(fresh.oems);
          }
        });
      }
    } else {
      resetForm();
    }
    return () => { mounted = false; };
  }, [initialValues, form, resetForm]);

  // сабмит через onFinish — без ручного validateFields()
  const handleFinish = async (values: any) => {
    try {
      const imagesField = values.images || [];
      const base64Images = await Promise.all(
        imagesField.map(async (file: any) => {
          if (file?.originFileObj) return await toBase64(file.originFileObj);
          return file?.url || file?.thumbUrl;
        })
      );

      const originalServerUrls = (initialValues?.images ?? [])
        .filter((img: string) => typeof img === 'string' && !img.startsWith('data:'));
      const _deletedImgs = originalServerUrls.filter((img: string) => !base64Images.includes(img));

      const newItem: SteeringClockItem = {
        id: initialValues?.id || crypto.randomUUID(),
        key: initialValues?.key || Date.now().toString(),
        ...values,
        images: base64Images,
        references: refList,
        oems: oemList,
        _deletedImgs,
      } as any;

      await onAdd(newItem);
      message.success('Товар добавлен или обновлён!');
      resetForm();
    } catch {
      message.error('Заполните все обязательные поля');
    }
  };

  return (
    <Form
      form={form}
      name="steeringClockForm"
      layout="vertical"
      preserve={false}
      onFinish={handleFinish}
    >
      <Form.Item label="Бренд" rules={[{ required: true }]} name="brand">
        <Input />
      </Form.Item>

      <Form.Item label="Номер детали" rules={[{ required: true }]} name="partNumber">
        <Input />
      </Form.Item>

      <Form.Item label="SKU" name="sku">
        <Input maxLength={10} />
      </Form.Item>

      <Form.Item label="Модель" rules={[{ required: true }]} name="model">
        <Input />
      </Form.Item>

      <Form.Item label="Цена" rules={[{ required: true }, nonNegativeRule('Цена')]} name="price">
        <Input type="number" />
      </Form.Item>

      <Form.Item label="Количество (в наличии)" rules={[{ required: true }, nonNegativeRule('Количество')]} name="quantity">
        <Input type="number" />
      </Form.Item>

      <Form.Item label="Количество разьёмов" rules={[{ required: true }, nonNegativeRule('Кол-во штекеров')]} name="connectorCount">
        <Input type="number" />
      </Form.Item>

      <Form.Item label="Количество проводов (для Подушки Безопасности)" rules={[{ required: true }, nonNegativeRule('Кол-во проводов')]} name="airbagWireCount">
        <Input type="number" />
      </Form.Item>

      <Form.Item name="original" label="Тип товара" rules={[{ required: true }]}>
        <Select>
          <Option value="original">Оригинал</Option>
          <Option value="copy">Копия</Option>
        </Select>
      </Form.Item>

      <RefOemManager title="REF" data={refList} setData={setRefList} />
      <RefOemManager title="OEM" data={oemList} setData={setOemList} />
      <ImageUploader />

      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={resetForm}>Очистить форму</Button>
        <Button type="primary" htmlType="submit">
          Добавить Продукт
        </Button>
      </div>
    </Form>
  );
};

export default SteeringClockForm;
